import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shared/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shared/table';
import { ArrowUpDown, HelpCircle } from 'lucide-react';
import { normalizeScores } from '@/utils/normalization';
import OpportunityRow from './opportunities/OpportunityRow';
import { Button } from '@/components/shared/button';
import { InfoIconWithTooltip } from '@/components/shared/InfoIconWithTooltip';

import { OpportunityScore } from '@/types/opportunity';

type SortKey = 'valueScore' | 'feasibilityScore' | 'finalScore';

interface RankedListProps {
  scoredOpportunities: OpportunityScore[];
  onOpenAboutScore: () => void;
}

const RankedList = ({ scoredOpportunities, onOpenAboutScore }: RankedListProps) => {
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'ascending' | 'descending' }>({ key: 'finalScore', direction: 'descending' });

  // Debug logging to see how many opportunities we're working with
  console.log('RankedList - scoredOpportunities length:', scoredOpportunities?.length);
  console.log('RankedList - scoredOpportunities names:', scoredOpportunities?.map(opp => opp.Name));

  // Calculate ranks based on final score (50% value + 50% feasibility)
  // This determines the rank number (1-21) that will always be shown
  const finalScoreRankMap = useMemo(() => {
    // Create a map to store ranks by opportunity name
    const rankMap = new Map<string, number>();
    
    // Sort all opportunities by final score (descending)
    const sortedByFinalScore = [...scoredOpportunities].sort((a, b) => {
      return b.finalScore - a.finalScore; // Descending order
    });
    
    // Assign ranks (1-based) based on position in the sorted array
    sortedByFinalScore.forEach((opportunity, index) => {
      rankMap.set(opportunity.Name, index + 1);
    });
    
    return rankMap;
  }, [scoredOpportunities]);

  // Sort opportunities based on current sort config
  const sortedOpportunities = useMemo(() => {
    const sortableItems = [...scoredOpportunities];
    sortableItems.sort((a, b) => {
      let aValue, bValue;
      if (sortConfig.key === 'finalScore') {
        aValue = a.finalScore;
        bValue = b.finalScore;
      } else {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    return sortableItems;
  }, [scoredOpportunities, sortConfig]);

  const requestSort = (key: SortKey) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: SortKey) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    return sortConfig.direction === 'ascending' ? (
      <ArrowUpDown className="ml-2 h-4 w-4 rotate-180" />
    ) : (
      <ArrowUpDown className="ml-2 h-4 w-4" />
    );
  };

  const handleOpenAboutScore = () => {
    onOpenAboutScore();
  };

  if (!scoredOpportunities || scoredOpportunities.length === 0) {
    return (
      <div className="pt-20">
        <div className="text-center space-y-2">
          <div className="inline-block px-5 py-2 text-lg font-medium text-white bg-gradient-to-r from-[#CA0079] to-[#F1AB11] rounded-full shadow-lg font-geist-mono mb-2">
            EXPLORE MORE
          </div>
          <h2 className="text-3xl md:text-3xl text-gray-800 pb-16 font-medium">See all opportunities and how they rank against each other</h2>
        </div>
        <Card className="p-8 bg-white border border-gray-200 shadow-sm">
          <div className="text-center text-gray-500">
            No opportunities available to display.
          </div>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="pt-20">
      <div className="text-center space-y-2">
        <div className="inline-block px-5 py-2 text-lg font-medium text-white bg-gradient-to-r from-[#CA0079] to-[#F1AB11] rounded-full shadow-lg font-geist-mono mb-2">
            EXPLORE MORE
        </div>
        <h2 className="text-3xl md:text-3xl text-gray-800 pb-16 font-medium">See all opportunities and how they rank against each other</h2>
      </div>
      <Card className="p-0 bg-white border border-gray-200 shadow-sm overflow-hidden">
        <div className="pt-4 bg-[#3B50A2] bg-opacity-10">
          <Table>
            <TableHeader className="border-b-2 border-[#3B50A2]">
              <TableRow className="hover:bg-transparent h-14">
                {/* Rank - Always first */}
                <TableHead className="w-[120px] font-semibold text-[#3B50A2] align-middle text-center text-lg pb-4">Rank</TableHead>
                
                {/* Opportunity - Always second */}
                <TableHead className="font-semibold text-[#3B50A2] align-middle text-left text-lg pb-4">Opportunity</TableHead>
                
                {/* Final Score - Third on mobile, last on desktop */}
                <TableHead className="lg:hidden w-[150px] font-semibold text-[#3B50A2] align-top text-left text-lg">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" onClick={() => requestSort('finalScore')} className="pl-4 text-[#3B50A2] text-lg font-semibold hover:border-2 hover:bg-clear hover:border-[#3B50A2] hover:text-[#3B50A2]">
                      Final Score {getSortIcon('finalScore')}
                    </Button>
                    <InfoIconWithTooltip 
                      tooltipText="Learn more about how this score is defined and interpreted."
                      onClick={handleOpenAboutScore}
                      className="mt-1 text-[#3B50A2] hover:text-[#CA0079] transition-colors"
                    />
                  </div>
                </TableHead>
                
                {/* Value Score - Fourth on mobile, third on desktop */}
                <TableHead className="lg:hidden w-[150px] font-semibold text-[#3B50A2] align-top text-left text-lg">
                  <Button variant="ghost" onClick={() => requestSort('valueScore')} className="pl-4 text-[#3B50A2] text-lg font-semibold hover:border-2 hover:bg-clear hover:border-[#3B50A2] hover:text-[#3B50A2]">
                    Value Score {getSortIcon('valueScore')}
                  </Button>
                </TableHead>
                
                {/* Feasibility Score - Fifth on mobile, fourth on desktop */}
                <TableHead className="lg:hidden w-[180px] font-semibold text-[#3B50A2] align-top text-left text-lg">
                  <Button variant="ghost" onClick={() => requestSort('feasibilityScore')} className="pl-4 text-[#3B50A2] text-lg font-semibold hover:border-2 hover:bg-clear hover:border-[#3B50A2] hover:text-[#3B50A2]">
                    Feasibility Score {getSortIcon('feasibilityScore')}
                  </Button>
                </TableHead>
                
                {/* Desktop-only columns */}
                <TableHead className="hidden lg:table-cell w-[150px] font-semibold text-[#3B50A2] align-top text-left text-lg">
                  <Button variant="ghost" onClick={() => requestSort('valueScore')} className="pl-4 text-[#3B50A2] text-lg font-semibold hover:border-2 hover:bg-clear hover:border-[#3B50A2] hover:text-[#3B50A2]">
                    Value Score {getSortIcon('valueScore')}
                  </Button>
                </TableHead>
                
                <TableHead className="hidden lg:table-cell w-[180px] font-semibold text-[#3B50A2] align-top text-left text-lg">
                  <Button variant="ghost" onClick={() => requestSort('feasibilityScore')} className="pl-4 text-[#3B50A2] text-lg font-semibold hover:border-2 hover:bg-clear hover:border-[#3B50A2] hover:text-[#3B50A2]">
                    Feasibility Score {getSortIcon('feasibilityScore')}
                  </Button>
                </TableHead>
                
                <TableHead className="hidden lg:table-cell w-[150px] font-semibold text-[#3B50A2] align-top text-left text-lg">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" onClick={() => requestSort('finalScore')} className="pl-4 text-[#3B50A2] text-lg font-semibold hover:border-2 hover:bg-clear hover:border-[#3B50A2] hover:text-[#3B50A2]">
                      Final Score {getSortIcon('finalScore')}
                    </Button>
                    <InfoIconWithTooltip 
                      tooltipText="Learn more about how this score is defined and interpreted."
                      onClick={handleOpenAboutScore}
                      className="mt-1 text-[#3B50A2] hover:text-[#CA0079] transition-colors"
                    />
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
          <TableBody className="bg-white">
            {sortedOpportunities.map((opportunity, index) => (
              <OpportunityRow 
                key={opportunity.Name}
                opportunity={opportunity}
                index={index}
                rank={finalScoreRankMap.get(opportunity.Name) || index + 1}
                isFirstItem={index === 0}
              />
            ))}
          </TableBody>
        </Table>
        </div>
        <div className="p-6 bg-white"></div>
      </Card>
    </div>
  );
};

export default RankedList;